import {
  closestCenter,
  DndContext,
  DndContextProps,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Stack, styled } from '@mui/material';
import useId from '@mui/material/utils/useId';
import { useTranslation, TFunction } from 'react-i18next';
import { DragIndicator } from '@mui/icons-material';
import { useMemo, useCallback, useState } from 'react';
import { flexCenterProps } from '@/util';

type Props<T> = {
  onEnd?: (items: T[], oldIndex: number, newIndex: number) => void;
  items: T[];
  getItemKey: (item: T, index: number) => string | number;
  renderer: (item: T, index: number) => JSX.Element;
  getItemAriaLabel: (item: T, index: number) => string;
  dense?: boolean;
  useFullAreaForDrag?: boolean;
  divider?: JSX.Element;
};

export function LtSorter<T extends string | object>({
  onEnd,
  items,
  getItemKey,
  renderer,
  dense,
  getItemAriaLabel,
  useFullAreaForDrag,
  divider,
}: Props<T>) {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  const { itemIds, idToIndexMap } = useMemo(() => {
    const ids = items.map((item, index) => getItemKey(item, index).toString());
    const map = new Map<string, number>();
    ids.forEach((id, index) => map.set(id, index));
    return { itemIds: ids, idToIndexMap: map };
  }, [items, getItemKey]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveId(null);

      if (!over) return;

      const oldIndex = idToIndexMap.get(active.id.toString());
      const newIndex = idToIndexMap.get(over.id.toString());

      if (oldIndex === undefined || newIndex === undefined) return;
      if (oldIndex === newIndex) return;

      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      onEnd?.(reorderedItems, oldIndex, newIndex);
    },
    [items, idToIndexMap, onEnd],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const accessibility: DndContextProps['accessibility'] = useMemo(() => {
    return {
      announcements: {
        onDragStart({ active }) {
          const activeItemIndex = idToIndexMap.get(active.id.toString());
          if (activeItemIndex === undefined) return;

          return t('ariaDnd.onDragStart', {
            itemName: getItemAriaLabel(items[activeItemIndex], activeItemIndex),
            position: activeItemIndex + 1,
          });
        },
        onDragOver() {
          return undefined;
        },
        onDragEnd({ active, over }) {
          const activeItemIndex = idToIndexMap.get(active.id.toString());
          const overItemIndex = over ? idToIndexMap.get(over.id.toString()) : undefined;
          if (activeItemIndex === undefined || overItemIndex === undefined) return;
          return t('ariaDnd.onDragEnd', {
            itemName: getItemAriaLabel(items[activeItemIndex], activeItemIndex),
            fromPosition: activeItemIndex + 1,
            toPosition: overItemIndex + 1,
          });
        },
        onDragCancel({ active }) {
          const activeItemIndex = idToIndexMap.get(active.id.toString());
          if (activeItemIndex === undefined) return;

          return t('ariaDnd.onDragCancel', {
            itemName: getItemAriaLabel(items[activeItemIndex], activeItemIndex),
            position: activeItemIndex + 1,
          });
        },
      },
    };
  }, [items, idToIndexMap, getItemAriaLabel, t]);

  const dragDescriptionId = useId() || '';

  const dragOverlayContent = useMemo(() => {
    if (!activeId) return null;
    const activeIndex = idToIndexMap.get(activeId);
    if (activeIndex === undefined) return null;
    const activeItem = items[activeIndex];
    return (
      <ItemWrapper component='div' style={{ cursor: 'grabbing' }}>
        {useFullAreaForDrag ? (
          <div>{renderer(activeItem, activeIndex)}</div>
        ) : (
          <Stack direction='row' alignItems='center' spacing={{ xs: 0.25, md: 0.5 }}>
            <Box {...flexCenterProps}>
              <DragIndicator />
            </Box>
            <Box flex={1}>{renderer(activeItem, activeIndex)}</Box>
          </Stack>
        )}
      </ItemWrapper>
    );
  }, [activeId, items, idToIndexMap, useFullAreaForDrag, renderer]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        accessibility={accessibility}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <ul>
            {items?.map((item: T, index: number) => {
              const itemId = getItemKey(item, index).toString();
              return (
                <SortableItem
                  key={itemId}
                  itemId={itemId}
                  item={item}
                  index={index}
                  renderer={renderer}
                  getItemAriaLabel={getItemAriaLabel}
                  dense={dense}
                  useFullAreaForDrag={useFullAreaForDrag}
                  divider={index > 0 ? divider : undefined}
                  dragDescriptionId={dragDescriptionId}
                  t={t}
                  isDragging={activeId === itemId}
                />
              );
            })}
          </ul>
        </SortableContext>
        <DragOverlay>{dragOverlayContent}</DragOverlay>
        <div id={dragDescriptionId} style={{ display: 'none' }}>
          {t('ariaDnd.draggableDescription')}
        </div>
      </DndContext>
    </>
  );
}

export default LtSorter;

type SortableItemProps<T> = {
  itemId: string;
  item: T;
  index: number;
  renderer: (item: T, index: number) => JSX.Element;
  getItemAriaLabel: (item: T, index: number) => string;
  dense?: boolean;
  useFullAreaForDrag?: boolean;
  divider?: JSX.Element;
  dragDescriptionId: string;
  t: TFunction;
  isDragging: boolean;
};

const SortableItem = function SortableItem<T extends string | object>({
  itemId,
  item,
  index,
  renderer,
  getItemAriaLabel,
  dense,
  useFullAreaForDrag,
  divider,
  dragDescriptionId,
  t,
  isDragging,
}: SortableItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: itemId,
  });

  const renderedContent = useMemo(() => renderer(item, index), [renderer, item, index]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0 : 1,
    cursor: isDragging ? 'grabbing' : useFullAreaForDrag ? 'grab' : 'default',
  };

  const dragHandleProps = useFullAreaForDrag
    ? {
        ...listeners,
        'aria-roledescription': t('ariaDnd.draggableItem'),
        'aria-describedby': dragDescriptionId,
        role: 'button' as const,
      }
    : {};

  return (
    <>
      {divider}
      <ItemWrapper
        component='li'
        sx={!dense ? { pb: 2 } : undefined}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...dragHandleProps}
      >
        {useFullAreaForDrag ? (
          <div>{renderedContent}</div>
        ) : (
          <Stack direction='row' alignItems='center' spacing={{ xs: 0.25, md: 0.5 }}>
            <Box
              {...flexCenterProps}
              {...listeners}
              sx={{ '&:focus-visible': { outline: '3px solid currentColor' }, cursor: 'grab' }}
              aria-label={
                getItemAriaLabel(item, index) + ' - ' + (t('ariaDnd.draggableItem') || '')
              }
              aria-describedby={dragDescriptionId}
            >
              <DragIndicator />
            </Box>
            <Box flex={1}>{renderedContent}</Box>
          </Stack>
        )}
      </ItemWrapper>
    </>
  );
} as <T extends string | object>(props: SortableItemProps<T>) => JSX.Element;

const ItemWrapper: typeof Box = styled(Box)(({ theme }) => ({
  '&:focus-visible': {
    '> div': {
      outline: `3px solid ${theme.palette.primary.main}`,
    },
  },
}));
